// src/canvas/components/ThreatMitigationsInlineV2.jsx
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Stack, Text, TextInput, Group, Button, Paper, ActionIcon, Badge } from '@mantine/core';
import { IconPlus, IconEdit, IconTrash, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { useDispatch } from 'react-redux';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { v4 as uuid } from 'uuid';
import { saveSecurityControl } from '../../redux/slices/securitySlice';
import { authFetch } from '../../utils/authFetch';

/**
 * ThreatMitigationsInlineV2 - Inline editable mitigation actions for threat scenarios
 * Matches RiskMitigationsInlineV2 pattern for security controls
 */
function ThreatMitigationsInlineV2({ threat, modelId, assessmentUuid, canEdit }) {
  const dispatch = useDispatch();
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showAddAction, setShowAddAction] = useState(false);
  const [newActionName, setNewActionName] = useState('');
  const [saving, setSaving] = useState(false);

  // Start editing control name
  const handleStartEdit = useCallback((control) => {
    setEditingId(control.controlId);
    setEditText(control.name || '');
  }, []);

  // Save control name
  const handleSaveControl = useCallback(
    async (control) => {
      if (!editText.trim()) {
        notifications.show({ title: 'Error', message: 'Action name required', color: 'red' });
        return;
      }

      setSaving(true);
      try {
        await dispatch(
          saveSecurityControl({
            controlData: { ...control, name: editText.trim() },
            modelId,
            scenarioId: threat.threatId,
            assessmentUuid,
          })
        ).unwrap();

        notifications.show({ title: 'Success', message: 'Action updated', color: 'green' });
        setEditingId(null);
        setEditText('');
      } catch (error) {
        console.error('[ThreatMitigationsInlineV2] Failed to save control:', error);
        notifications.show({ title: 'Error', message: 'Failed to save action', color: 'red' });
      } finally {
        setSaving(false);
      }
    },
    [editText, dispatch, modelId, threat.threatId, assessmentUuid]
  );

  // Cancel edit
  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText('');
  }, []);

  // Add new action
  const handleAddAction = useCallback(async () => {
    if (!newActionName.trim()) {
      notifications.show({ title: 'Error', message: 'Action name required', color: 'red' });
      return;
    }

    setSaving(true);
    try {
      // Save to database and Redux (note: endpoint is saveSecurityControls plural)
      await dispatch(
        saveSecurityControl({
          controlData: {
            controlId: uuid(),
            name: newActionName.trim(),
            description: '',
            status: 'proposed',
          },
          modelId,
          scenarioId: threat.threatId,
          assessmentUuid,
        })
      ).unwrap();

      notifications.show({ title: 'Success', message: 'Action added', color: 'green' });
      setNewActionName('');
      setShowAddAction(false);
    } catch (error) {
      console.error('[ThreatMitigationsInlineV2] Failed to add action:', error);
      notifications.show({ title: 'Error', message: 'Failed to add action', color: 'red' });
    } finally {
      setSaving(false);
    }
  }, [newActionName, dispatch, modelId, threat.threatId, assessmentUuid]);

  // Delete control
  const handleDeleteControl = useCallback(
    (control) => {
      modals.openConfirmModal({
        title: 'Delete Action',
        children: (
          <Text size="sm">
            Delete "{control.name}"?
          </Text>
        ),
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          try {
            // Database delete
            const response = await authFetch('/.netlify/functions/deleteSecurityControl', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ controlId: control.controlId }),
            });

            if (!response.ok) {
              throw new Error('Failed to delete control');
            }

            // Refresh Redux by fetching controls again
            const fetchResponse = await authFetch('/.netlify/functions/fetchSecurityControls', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                modelId,
                assessmentUuid,
                scenarioId: threat.threatId,
              }),
            });

            if (fetchResponse.ok) {
              // Redux will be updated by the fetch thunk
              notifications.show({ title: 'Success', message: 'Action deleted', color: 'green' });
            }
          } catch (error) {
            console.error('[ThreatMitigationsInlineV2] Failed to delete control:', error);
            notifications.show({ title: 'Error', message: 'Failed to delete', color: 'red' });
          }
        },
      });
    },
    [modelId, assessmentUuid, threat.threatId]
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Mitigation Actions
        </Text>
        <Badge size="sm" variant="light">
          {threat.controls.length} Actions
        </Badge>
      </Group>

      {/* Action list */}
      {threat.controls.length === 0 && !showAddAction ? (
        <Paper withBorder p="sm" bg="gray.0">
          <Text size="xs" c="dimmed" fs="italic">
            No mitigation actions defined yet.
          </Text>
        </Paper>
      ) : (
        threat.controls.map((control) => (
          <Paper key={control.controlId} withBorder p="sm" bg="white">
            <Group justify="space-between">
              {editingId === control.controlId ? (
                <TextInput
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  style={{ flex: 1 }}
                  size="sm"
                  autoFocus
                />
              ) : (
                <Text size="sm" style={{ flex: 1 }}>
                  {control.name}
                </Text>
              )}

              {canEdit && (
                <Group gap={4}>
                  {editingId === control.controlId ? (
                    <>
                      <ActionIcon
                        size="sm"
                        color="green"
                        onClick={() => handleSaveControl(control)}
                        loading={saving}
                      >
                        <IconDeviceFloppy size={12} />
                      </ActionIcon>
                      <ActionIcon size="sm" color="gray" onClick={handleCancelEdit} disabled={saving}>
                        <IconX size={12} />
                      </ActionIcon>
                    </>
                  ) : (
                    <>
                      <ActionIcon size="sm" variant="subtle" onClick={() => handleStartEdit(control)}>
                        <IconEdit size={12} />
                      </ActionIcon>
                      <ActionIcon size="sm" color="red" variant="subtle" onClick={() => handleDeleteControl(control)}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </>
                  )}
                </Group>
              )}
            </Group>
          </Paper>
        ))
      )}

      {/* Add new action form */}
      {showAddAction && (
        <Paper withBorder p="sm" bg="blue.0">
          <Stack gap="xs">
            <TextInput
              placeholder="Describe the mitigation action..."
              value={newActionName}
              onChange={(e) => setNewActionName(e.target.value)}
              autoFocus
            />
            <Group gap="xs">
              <Button size="xs" onClick={handleAddAction} loading={saving}>
                Add
              </Button>
              <Button
                size="xs"
                variant="subtle"
                onClick={() => {
                  setShowAddAction(false);
                  setNewActionName('');
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}

      {canEdit && !showAddAction && (
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={12} />}
          onClick={() => setShowAddAction(true)}
        >
          Add Action
        </Button>
      )}
    </Stack>
  );
}

ThreatMitigationsInlineV2.propTypes = {
  threat: PropTypes.shape({
    threatId: PropTypes.string.isRequired,
    controls: PropTypes.arrayOf(
      PropTypes.shape({
        controlId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        status: PropTypes.string,
      })
    ),
  }).isRequired,
  modelId: PropTypes.string.isRequired,
  assessmentUuid: PropTypes.string.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default ThreatMitigationsInlineV2;
